/**
 * GoldFire Network (https://goldfire.me)
 * Copyright (c) 2012 - 2020, GoldFire Studios, Inc.
 * http://goldfirestudios.com
 */

import {store} from './client-create-app';
import {readCookie} from './utils';

// Include visibility API.
let Visibility;
if (typeof window !== 'undefined') {
  Visibility = require('visibilityjs');
}

/**
 * Receives new chat messages from the server and updates the display.
 * @param  {Object} data Data about the chat message.
 */
export const recChatMessage = (data) => {
  const msg = {
    cid: data.cid,
    msg: data.msg,
    player: data.player,
    t: new Date(data.t),
  };

  if (data.typing === true) {
    delete msg.msg;
    delete msg.t;
    msg.typing = true;
  } else if (data.typing === false) {
    store.commit('remChatTyping', {player: msg.player, scroll: true});
    return;
  }

  if (store.state.scroll.end) {
    store.commit('remChatTyping', {player: msg.player, scroll: false});
    store.commit('addChatMessage', msg);
  }
};

/**
 * Updates the toolbar message count.
 * @param  {Object} data         Data.
 * @param  {Number} data.unread  Number of unread messages.
 */
export const setToolbarMessages = (data) => {
  store.commit('setToolbarMessages', data.unread);
};

/**
 * Update the player status (on, off, away) and last online time.
 * @param  {Object} data status, t, uid
 */
export const playerStatus = (data) => {
  store.commit('addPlayerStatus', data);

  // If we have the friends list open, update it.
  if (store.state.toolbar.activeTab === 'chat' && store.state.chat.activeView === 'friends-view') {
    // Update the player status in friends cache.
    store.commit('updateFriendsCache', data);

    // If they are currently searching, don't update the list.
    if (store.state.panel.input && store.state.panel.input.value !== '') {
      return;
    }

    // Check if this friend is in the friends list already.
    const friends = store.state.friends.players;
    const index = friends.findIndex(user => user.uid === data.uid);

    // Replace the old value or add the new one.
    const items = friends.slice();
    const firstPage = (store.state.scroll.start);
    if (!firstPage && index >= 0) {
      items.splice(index, 1);
    } else if (index >= 0) {
      items[index] = store.state.friends.cache[data.uid];
    } else if (firstPage) {
      items.unshift(store.state.friends.cache[data.uid]);
    }

    // Update the store data.
    store.commit('setFriendsList', {items});
  }
};

/**
 * Update the unread thread count for forum category/categories.
 * @param  {Object} data categoryList
 */
export const forumCategoryUnreads = ({categoryList}) => {
  store.commit('setForumCategoryList', {categoryList});
};

/**
 * Update the unread thread count for forum category/categories.
 * @param  {Object} thread  new forum thread to insert
 */
export const newForumThread = ({thread, uid}) => {
  store.commit('insertNewForumThread', {thread, uid});
  if (uid === store.state.user.uid) {
    store.dispatch('viewForumThread', thread);
    // store.commit('incForumNotifications', -1);
  } else {
    store.commit('incForumNotifications', 1);
  }

  store.commit('updateThreadWithPost', {tid: thread._id, avatar: thread.avatar});
};


export const incrementUnreadCount = ({tid, avatar}) => {
  store.commit('incForumNotifications', 1);
  store.commit('updateThreadWithPost', {tid, avatar});
};

/**
 * Update a thread with a new post.
 * @param  {Object} options.post   New forum thread reply to insert.
 * @param  {Boolean} options.del   Whether or not this is a deletion.
 * @param  {Boolean} options.thread Whether or not the deletion is the whole thread.
 */
export const newThreadReply = ({post, del, thread}) => {
  const amAuthor = post.player.uid === store.state.user.uid;

  if (del) {
    if (thread) {
      store.dispatch('goBackView');
    } else {
      store.commit('removeThreadReply', post.id);
      if (!amAuthor) {
        store.commit('incForumNotifications', -1);
      }
    }
    return;
  }

  // Show notification to all users who are not the author of the new post
  if (!amAuthor) {
    store.commit('incForumNotifications', 1);
  }

  if (store.state.scroll.end) { // If at the end, just insert the post
    store.commit('insertThreadReply', {post});
    if (amAuthor) {
      store.commit('scrollToBottom');
    }
  } else if (amAuthor) { // Not at the end, but am the author, so jump to end
    store.commit('reloadForumThread');
  }
};

/**
 * Update a forum post (if it is loaded).
 * @param  {Object} options
 * @param  {String} options.pid      Post ID.
 * @param  {String} options.message  New post content.
 */
export const forumPostEdit = ({id, message}) => {
  store.commit('editForumPost', {id, message});
};

/**
 * Update the unread thread count for forum category/categories.
 * @param  {Object} gid   category id to load new unread count for.
 */
export const updateForumCategoryUnreadCount = () => {
  // TODO: this could be made more efficient to load only the unread count
  if (store.state.forums.activeView === 'forums-view' && store.state.toolbar.activeTab === 'forums') {
    store.dispatch('viewForumCategories');
  }

  store.dispatch('getForumCounter');
};

/**
 * Increment the view count for forum thread (on first post).
 */
export const forumThreadViews = () => {
  store.commit('incForumThreadViews');
};

/**
 * Update the active chat message thread.
 * @param  {Object} data Chat message data.
 */
export const updateChatList = (data) => {
  const firstPage = (store.state.scroll.start);

  // Find and update this chat message.
  const index = store.state.chat.messages.findIndex(msg => msg._id === data._id);

  // Remove yourself from the list of players.
  if (typeof data.name === 'undefined') {
    data.players = data.players.filter(player => player.uid !== store.state.user.uid);
  }

  // Replace the old value or add the new one.
  const chats = store.state.chat.messages.slice();
  if (typeof data.name === 'undefined') {
    if (!firstPage && index >= 0) {
      chats.splice(index, 1);
    } else if (index >= 0) {
      chats[index] = data;
    } else if (firstPage) {
      chats.unshift(data);
    }
  } else if (index >= 0) {
    chats[index].name = data.name;
  }

  // Update the store data.
  store.commit('setChatThreadList', {items: chats});
};

/**
 * Receive a notification, show popup and update counters.
 * @param  {Object} data Notification data.
 */
export const recNotification = (data) => {
  // Don't show anything if we are on chat tab and this is a chat notice.
  const {state} = store;
  const isChatTab = state.toolbar.activeTab === 'chat';
  const isChatListView = state.chat.activeView === 'chat-list-view';
  const isChatMessageView = state.chat.activeView === 'chat-message-view';
  if (data.type === 'chat' && isChatTab && (isChatListView || isChatMessageView)) {
    return;
  }

  // Show the popup notification.
  if (Visibility) {
    store.commit('addNotificationToQueue', data);
  }

  // Don't continue if this is a chat notice.
  if (data.type === 'chat') {
    return;
  }

  // Add the notification to the list.
  const isLauncher = state.toolbar.activeTab === 'launcher';
  const isNotifications = state.launcher.activeView === 'notifications-view';
  const isGame = state.launcher.notificationsId === data.gid;
  if (isLauncher && isNotifications && isGame) {
    // Add notification to the list if we have the first page in memory.
    // Otherwise, increment extra so we stay in sync with the server.
    if (store.state.scroll.start) {
      const notifications = [data, ...state.launcher.notifications];
      store.commit('setNotifications', {items: notifications});
    } else {
      store.commit('setScrollData', {extra: store.state.scroll.extra + 1});
    }
  } else {
    // Update the notification count in the toolbar.
    store.commit('incToolbarNotifications');

    // Update the notification counts for this game.
    store.commit('incGameNotifications', {notification: data});
  }
};

/**
 * Increment the number of likes on an update.
 * @param  {Object} data Data about the update to like.
 */
export const toggleLike = (data) => {
  store.commit('toggleLike', data);
};

/**
 * Add a comment to a social feed update.
 * @param  {Object} data Data about the comment.
 */
export const updateCommented = (data) => {
  store.commit('concatComment', data);
};

/**
 * Add a new social feed update to the list.
 * @param  {Object} data Update to post.
 */
export const socialFeedUpdatePosted = (data) => {
  const isProfilePlayerPost = data.player.uid === store.state.launcher.profile.uid;
  if ((store.state.toolbar.activeTab === 'launcher' && !isProfilePlayerPost) || !store.state.scroll.start) {
    return;
  }

  const updates = [Object.assign(data, {commenting: false})];
  store.commit('setSocialFeedUpdates', {items: updates, prepend: true});

  updates.forEach((update) => {
    store.dispatch('sub', `updateLiked:${update._id}`);
    store.dispatch('sub', `updateCommented:${update._id}`);
  });
};

/**
 * A social feed post was deleted.
 * @param  {String} id  ID of update to remove.
 */
export const socialFeedUpdateRemoved = ({id}) => {
  store.commit('removeSocialFeedUpdate', id);
};

/**
 * A social feed post comment was deleted.
 * @param  {String}    id  ID of update with comment to remove.
 * @param  {String}    uid ID of commenter.
 * @param  {String}    t   Time comment was posted.
 */
export const socialFeedCommentRemoved = ({id, uid, t}) => {
  store.commit('removeSocialFeedComment', {id, uid, t});
};

/**
 * Update the social feed unread counter.
 * {Number} num  optional  Amount by which to increment the counter.
 */
export const socialFeedCounter = ({num = 1}) => {
  if (store.state.forums.activeView !== 'social-feed-view') {
    store.commit('incSocialFeedCounter', num);
  }
};

/**
 * Update the social feed unread counter on profile.
 * {Number} num  optional  Amount by which to increment the counter.
 */
export const socialFeedCounterProfile = ({num = 1}) => {
  store.commit('incProfileSocialFeedCounter', num);
};

/**
 * Receive a thread vote and update the data.
 * @param  {Object} data Contains the answer they voted for.
 */
export const threadPollVote = (data) => {
  const answer = parseInt(data.answer, 10);
  const curState = store.state.forums.activeThread.poll;

  if (typeof answer === 'number' && curState) {
    const newState = Object.assign({}, curState);
    newState.v[answer] += 1;
    store.commit('setThreadPollData', newState);
  }
};

/**
 * Receive the status of an online/offline player on a specific game.
 * @param  {Object} data Data about the player.
 */
export const setOnlineGame = (data) => {
  // They have disconnected if they have no avatar.
  if (data.uid && !data.avatar) {
    store.commit('remOnlinePlayers', data);
  } else if (data.uid && data.avatar) {
    store.commit('addOnlinePlayers', data);
  }
};

/**
 * Update the online user counter for a specific game.
 * @param  {Object} data Data about the game and status.
 */
export const setOnlineCounter = (data) => {
  if (!data.on) {
    store.commit('incGameOnlineCounters', {game: data.gid, val: -1});
  } else {
    store.commit('incGameOnlineCounters', {game: data.gid, val: 1});
  }
};

/**
 * Receive a completed PayPal transaction notice.
 * @param  {Boolean} options.pending If this is a pending message, mark it as such.
 */
export const completePaypal = ({pending}) => {
  store.commit('setPaypalStatus', pending ? 'pending' : true);
};

/**
 * Receive a completed Steam transaction notice.
 * @param  {Boolean} options.success If this is a successful purchase.
 */
export const completeSteam = ({success}) => {
  store.commit('changeView', success ? 'shop-success-view' : 'shop-pending-view');
};

/**
 * Added a game.
 * @param  {Object} options.gid         Game ID.
 * @param  {Object} options.name        Game name.
 * @param  {Object} options.medalCount  How many medals are available in the game.
 */
export const gameAdded = ({gid, name, medalCount}) => {
  store.commit('addProfileGame', {gid, name, medalCount});
};

/**
 * Added a friend.
 * @param  {Object} options.friend          Friend who was added.
 * @param  {String} options.friend.username
 * @param  {String} options.friend.avatar
 * @param  {Number} options.friend.t
 * @param  {String} options.friend.status
 * @param  {String} options.friend.role
 */
export const friendAdded = ({friend}) => {
  store.commit('addProfileFriend', friend);
};

/**
 * Removed a friend.
 * @param  {String} options.friendId  UID of friend to remove.
 */
export const friendRemoved = ({friendId}) => {
  store.commit('removeProfileFriend', friendId);
};

/**
 * Earned a medal.
 * @param  {Number} options.gid    Game to which this medal belongs.
 * @param  {String} options.img    Medal ID/image.
 * @param  {String} options.title  Name of medal.
 */
export const medalEarned = ({gid, img, title}) => {
  store.commit('addProfileMedal', {gid, img, title});
};

/**
 * Earned progress toward a medal.
 * @param  {Number} options.gid  Game to which this medal belongs.
 * @param  {String} options.img  Medal ID/image.
 * @param  {Number} options.p    Progress (decimal between 0 and 1).
 */
export const medalUpdated = ({gid, img, p}) => {
  store.commit('updateMedalProgress', {gid, img, p});
};

/**
 * Check if the user has a session cookie. If not, reload the page.
 */
export const reAuth = () => {
  window.location.reload(false);
};
